import { useKeycloak } from '@react-keycloak/web';
import axios from 'axios';
import { type KeycloakConfig } from 'keycloak-js';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { CoreJsonAPIResponse } from './CoreJsonAPIResponse';
import CScrollToTop from './components/CScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalSnackbar from './components/GlobalSnackbar';
import ProtectedRoute from './components/PageGates/AuthRequiredRoute';
import GlobalLoaderMask from './components/general/GlobalLoaderMask';
import XDOutlet from './components/routing/XDOutlet';
import { CMultifundFactsheetRoles } from './constants/CMultifundFactsheetRoles';
import { ELocalStorageItems } from './enums/ELocalStorageItems';
import { EUserRoles } from './enums/EUserRoles';
import { PageRoutes } from './enums/enums';
import flattenLanguageJson from './helpers/helper';
import { useAppDispatch, useAppSelector } from './hooks/hooks';
import { type TLocales } from './models/TLocales';
import Dashboard from './pages/Dashboard';
import PageNotFound from './pages/PageNotFound';
import BacktestEntry from './pages/backtest/BacktestEntry';
import BacktestIndex from './pages/backtest/BacktestIndex';
import BacktestOutlet from './pages/backtest/BacktestOutlet';
import CacheClearPage from './pages/cache/CacheIndex';
import CacheManagementOutlet from './pages/cache/CacheOutlet';
import PCalculatorOutlet from './pages/calculator/PCalculatorOutlet';
import PCalculatorIndex from './pages/calculator/index/PCalculatorIndex';
import PContactIndex from './pages/contact/PContactIndex';
import PContactOutlet from './pages/contact/PContactOutlet';
import { PDownloadCenterIndex } from './pages/downloadCenter/PDownloadCenterIndex';
import PDownloadCenterOutlet from './pages/downloadCenter/PDownloadCenterOutlet';
import PTermsOfUseIndex from './pages/footer/index/PTermsOfUseIndex';
import PLocalisationOutlet from './pages/localisation/PLocalisationOutlet';
import PLocalisationTableView from './pages/localisation/PLocalisationTableView';
import PLocalisationEntryPage from './pages/localisation/localisationEntryPage/PLocalisationEntryPage';
import PMultifundFactsheetOutlet from './pages/multifundFactsheet/PMultifundFactsheetOutlet';
import { PMultifundFactsheetIndex } from './pages/multifundFactsheet/[simpleFundKey]/PMultifundFactsheetIndex';
import PMultifundFactsheetPerformance from './pages/multifundFactsheet/[simpleFundKey]/PMultifundFactsheetPerformance';
import NewsIndex from './pages/news/NewsIndex';
import ArticlesEntry from './pages/news/articles/ArticlesEntry';
import ArticlesIndex from './pages/news/articles/ArticlesIndex';
import PNotificationsIndex from './pages/notifications/PNotificationsIndex';
import PNotificationsOutlet from './pages/notifications/PNotificationsOutlet';
import OpsTemporaryRedirectView from './pages/opsNews/OpsTemporaryRedirectView';
import PPortfolioManagementOutlet from './pages/opsNews/PPortfolioManagementOutlet';
import PPorfolioManagementEvents from './pages/opsNews/tabs/PPorfolioManagementEvents';
import PPorfolioManagementHedge from './pages/opsNews/tabs/PPorfolioManagementHedge';
import PPorfolioManagementMisc from './pages/opsNews/tabs/PPorfolioManagementMisc';
import PPortfolioManagementPerformance from './pages/opsNews/tabs/PPortfolioManagementPerformance';
import { PPlaygroundIndex } from './pages/playground/PPlaygroundIndex';
import { PPlaygroundIndex2 } from './pages/playground/PPlaygroundIndex2';
import { PPlaygroundIndex3 } from './pages/playground/PPlaygroundIndex3';
import PDelta from './pages/publicFactsheet/PDelta';
import PESG from './pages/publicFactsheet/PESG';
import PFactorExposure from './pages/publicFactsheet/PFactorExposure';
import PFactsheetIndex from './pages/publicFactsheet/PFactsheetIndex';
import PFactsheetOutlet from './pages/publicFactsheet/PFactsheetOutlet';
import PPerformance from './pages/publicFactsheet/PPerformance';
import PPerformanceAttribution from './pages/publicFactsheet/PPerformanceAttribution';
import PSectorExposure from './pages/publicFactsheet/PSectorExposure';
import PublicFactsheetIndex from './pages/publicFactsheet/PublicFactsheetIndex';
import PublishingIndex from './pages/publishing/PublishingIndex';
import CreateNewFactsheetLink from './pages/publishing/factsheetManagement/CreateNewFactsheetLink';
import FactsheetManagementEntry from './pages/publishing/factsheetManagement/FactsheetManagementEntry';
import FactsheetManagementIndex from './pages/publishing/factsheetManagement/FactsheetManagementIndex';
import PFactsheetInquiryLinkIndex from './pages/publishing/factsheetManagement/PFactsheetInquiryLinkIndex';
import PFactsheetLinkIndex from './pages/publishing/factsheetManagement/PFactsheetLinkIndex';
import SingleAccessLinkInquiryView from './pages/publishing/factsheetManagement/SingleAccessLinkInquiryView';
import PSectorPerformanceOutlet from './pages/sectorPerformance/PSectorPerformanceOutlet';
import PSectorPerformanceIndex from './pages/sectorPerformance/index/PSectorPerformanceIndex';
import PSignUpIndex from './pages/signUp/PSignUpIndex';
import PSignUpOutlet from './pages/signUp/PSignUpOutlet';
import PSingleStockEntry from './pages/singleStock/PSingleStockEntry';
import PSingleStockIndex from './pages/singleStock/PSingleStockIndex';
import PTradeOrdersIndex from './pages/tradeOrders/PTradeOrdersIndex';
import PAllAppTracking from './pages/usageTracking/PAllAppTracking';
import PUserTrackingOutlet from './pages/usageTracking/PUserTrackingOutlet';
import PSingleAppTracking from './pages/usageTracking/singleAppTracking/PSingleAppTracking';
import AccountSelfManagement from './pages/userManagement/AccountSelfManagement';
import CreateUser from './pages/userManagement/CreateUser';
import OverviewOutlet from './pages/userManagement/OverviewOutlet';
import UserManagementIndex from './pages/userManagement/UserManagementIndex';
import UserManagementOutlet from './pages/userManagement/UserManagementOutlet';
import UserSecurityAndPrivacy from './pages/userManagement/UserSecurityAndPrivacy';
import UsersOverview from './pages/userManagement/UsersOverview';
import PSignUpRequestsIndex from './pages/userManagement/requests/PSignUpRequestsIndex';
import PSignUpRequestsOutlet from './pages/userManagement/requests/PSignUpRequestsOutlet';
import PSingleSignUpRequest from './pages/userManagement/requests/singleRequest/PSingleSignUpRequest';
import BacktestManagement from './pages/userManagement/userHub/BacktestManagement';
import SingleUserIndex from './pages/userManagement/userHub/UserHubIndex';
import UserHubOutlet from './pages/userManagement/userHub/UserHubOutlet';
import { SvFundServices } from './services/SvFundServices';
import I18nService from './services/i18nService';
import { updateFunds, updateIsTouchscreen, updateLocale, updateLocaleDict, updateSnackbar } from './store/appSlice';

// const factsheetRoutes = [PageRoutes.NEWS_OUTLET].map((e) => e.toString());

function App() {
  const { keycloak, initialized } = useKeycloak();
  const dispatch = useAppDispatch();

  const { processLoading, snackbar, locale, localeDict } = useAppSelector((state) => state.dashboard);

  axios.interceptors.request.use(
    (config) => {
      // Use the JWT from local storage because the one from the slice is often stale
      if (keycloak.authenticated) {
        // Only Update header when there is a JWT in the authentication slice
        config.headers.set('Authorization', `Bearer ${keycloak.token}`);
      }

      config.params = config.params ?? {};

      if (!('locale' in config.params)) {
        config.params.locale = localStorage.getItem(ELocalStorageItems.LOCALE) ?? 'de-DE';
      }

      return config;
    },
    async (error) => {
      return await Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (r) => {
      return r;
    },
    async (j) => {
      return j;
    }
  );

  const refreshFundList = async () => {
    const res = await SvFundServices.getAll();
    if (await res.isSuccess()) {
      dispatch(updateFunds({ funds: (await res.getData()) ?? [] }));
    }
  };

  useEffect(() => {
    const refreshConfig = () => {
      (async () => {
        const backendResponse = await axios.get('/api/keycloak');
        const cachedConfig = localStorage.getItem(ELocalStorageItems.KC_CONFIG);
        const latestConfig = JSON.stringify(await new CoreJsonAPIResponse<KeycloakConfig>(backendResponse).getData());
        if (cachedConfig !== latestConfig) {
          localStorage.setItem(ELocalStorageItems.KC_CONFIG, latestConfig);
          window.location.reload();
        }
        await refreshFundList();
      })();
    };

    const handleVisibilityChange = async () => {
      try {
        if (keycloak.authenticated) {
          await keycloak.updateToken();
        }
      } catch (e) {
        dispatch(
          updateSnackbar({
            message: 'You have been logged out due to inactivity, please refresh the page to log in again',
            severity: 'error'
          })
        );
      }
    };

    if (window.matchMedia('(any-pointer: coarse)').matches) {
      dispatch(updateIsTouchscreen({ isTouchscreen: true }));
    }

    window.setInterval(handleVisibilityChange, 4 * 60 * 1000);

    refreshConfig();

    dispatch(
      updateLocale({
        locale: (window.localStorage.getItem(ELocalStorageItems.LOCALE) as unknown as TLocales) ?? 'de-DE'
      })
    );

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const languageJson = await I18nService.loadLanguageJson();
      const dict = flattenLanguageJson(languageJson, locale ?? 'de-DE');
      dispatch(updateLocaleDict({ localeDict: dict }));
      await refreshFundList();
    })();
  }, [locale]);

  const requireAuth = (route: JSX.Element, requiredRoles?: EUserRoles[][]) => (
    <ProtectedRoute requiredRoles={requiredRoles}>{route}</ProtectedRoute>
  );

  /**
   * @deprecated Use requireAuth instead.
   * @param route
   */
  const requireAdminRole = (route: JSX.Element) => (
    <ProtectedRoute requiredRoles={[[EUserRoles.ADMIN]]}>{route}</ProtectedRoute>
  );

  /**
   * @deprecated Use requireAuth instead.
   * @param route
   */
  const requireStaffRole = (route: JSX.Element) => (
    <ProtectedRoute requiredRoles={[[EUserRoles.STAFF]]}>{route}</ProtectedRoute>
  );

  if (!initialized) {
    return <></>;
  }

  return (
    <ErrorBoundary>
      <CScrollToTop />
      <GlobalLoaderMask isVisible={processLoading > 0} />
      <GlobalSnackbar isVisible={snackbar.isVisible} />
      <div style={{ opacity: localeDict ? 1 : 0 }}>
        <Routes>
          <Route path={PageRoutes.DASHBOARD_OUTLET} element={<Dashboard />}>
            <Route path={PageRoutes.TERMS_OF_USE_INDEX} element={<PTermsOfUseIndex />} />
            <Route path={''} element={<Navigate to={PageRoutes.NEWS_OUTLET} replace />} />
            <Route path={PageRoutes.DASHBOARD_HOME} element={<Navigate to={PageRoutes.NEWS_OUTLET} replace />} />
            <Route
              path={PageRoutes.NEWS_OUTLET}
              element={<XDOutlet slug={PageRoutes.NEWS_OUTLET} title={localeDict?.['sidebar.news']} />}
            >
              <Route path={PageRoutes.NEWS_HOME} element={<NewsIndex />} />
              <Route
                path={PageRoutes.NEWS_ARTICLES_OUTLET}
                element={<XDOutlet slug={PageRoutes.NEWS_ARTICLES_OUTLET} title={localeDict?.newsArticles} />}
              >
                <Route path={PageRoutes.NEWS_ARTICLES_HOME} element={<ArticlesIndex />} />
                {/* <Route */}
                {/*   path={PageRoutes.NEWS_ARTICLES_HOME} */}
                {/*   element={<Navigate to={`/${PageRoutes.DASHBOARD_OUTLET}/${PageRoutes.NEWS_OUTLET}`} replace />} */}
                {/* /> */}
                <Route path={':slug'} element={<ArticlesEntry />} />
              </Route>
            </Route>
            <Route
              path={PageRoutes.SINGLE_STOCK_OUTLET}
              element={requireAuth(<XDOutlet slug={PageRoutes.SINGLE_STOCK_OUTLET} title={'Single Stock'} />)}
            >
              <Route path={PageRoutes.SINGLE_STOCK_INDEX} element={<PSingleStockIndex />} />
              <Route path={':identifier'} element={<PSingleStockEntry />} />
            </Route>
            <Route path={PageRoutes.CALCULATOR_OUTLET} element={requireAuth(requireStaffRole(<PCalculatorOutlet />))}>
              <Route path={PageRoutes.CALCULATOR_INDEX} element={<PCalculatorIndex />} />
            </Route>
            <Route path={PageRoutes.CONTACT_OUTLET} element={<PContactOutlet />}>
              <Route path={PageRoutes.CONTACT_INDEX} element={<PContactIndex />} />
            </Route>
            <Route path={PageRoutes.SECTOR_PERFORMANCE_OUTLET} element={requireAuth(<PSectorPerformanceOutlet />)}>
              <Route path={PageRoutes.SECTOR_PERFORMANCE_INDEX} element={<PSectorPerformanceIndex />} />
            </Route>
            <Route path={PageRoutes.BACKTEST_OUTLET} element={requireAuth(<BacktestOutlet />)}>
              <Route path={PageRoutes.BACKTEST_HOME} element={<BacktestIndex />} />
              <Route path={':allocation'} element={<BacktestIndex />} />
              <Route path={':allocation/:structure'} element={<BacktestIndex />} />
              <Route path={':allocation/:structure/:style'} element={<BacktestIndex />} />
              <Route path={':allocation/:structure/:style/:weight'} element={<BacktestIndex />} />
              <Route path={':allocation/:structure/:style/:weight/:backtestId'} element={<BacktestEntry />} />
            </Route>
            <Route
              path={PageRoutes.CACHE_MANAGEMENT_OUTLET}
              element={requireAuth(requireAdminRole(<CacheManagementOutlet />))}
            >
              <Route path={PageRoutes.CACHE_MANAGEMENT_INDEX} element={<CacheClearPage />} />
            </Route>
            <Route
              path={PageRoutes.USAGE_TRACKING_OUTLET}
              element={requireAuth(requireStaffRole(<PUserTrackingOutlet />))}
            >
              <Route path={PageRoutes.USAGE_TRACKING_INDEX} element={<PAllAppTracking />} />
              <Route path={':appName'} element={<PSingleAppTracking />} />
            </Route>
            <Route
              path={PageRoutes.I18N_CENTER_OUTLET}
              element={requireAuth(requireAdminRole(<PLocalisationOutlet />))}
            >
              <Route path={PageRoutes.I18N_CENTER_INDEX} element={<PLocalisationTableView />} />
              <Route path="new" element={<PLocalisationEntryPage />} />
              <Route path={':key'} element={<PLocalisationEntryPage />} />
            </Route>
            <Route
              path={PageRoutes.DOWNLOAD_CENTER_OUTLET}
              element={requireAuth(requireStaffRole(<PDownloadCenterOutlet />))}
            >
              <Route path={PageRoutes.DOWNLOAD_CENTER_INDEX} element={<PDownloadCenterIndex />} />
            </Route>
            <Route path={PageRoutes.SIGN_UP_OUTLET} element={<PSignUpOutlet />}>
              <Route path={PageRoutes.SIGN_UP_INDEX} element={<PSignUpIndex />} />
            </Route>
            <Route path={PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET}>
              <Route path={PageRoutes.PUBLIC_FACTSHEET_INDEX} element={<PublicFactsheetIndex />} />
              <Route path={':factsheetId'} element={<PFactsheetOutlet />}>
                <Route path={PageRoutes.PUBLIC_FACTSHEET_INDEX} element={<PFactsheetIndex />} />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_FACTOR_EXPOSURE} element={<PFactorExposure />} />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_SECTOR_EXPOSURE} element={<PSectorExposure />} />
                <Route
                  path={PageRoutes.PUBLIC_FACTSHEET_PERFORMANCE_ATTRIBUTION}
                  element={<PPerformanceAttribution />}
                />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_ESG} element={<PESG />} />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_DELTA} element={<PDelta />} />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_PERFORMANCE} element={<PPerformance />} />
              </Route>
            </Route>
            <Route
              path={PageRoutes.FACTSHEET_OUTLET}
              element={requireAuth(
                <XDOutlet slug={PageRoutes.FACTSHEET_OUTLET} title={'Factsheet'} />,
                CMultifundFactsheetRoles
              )}
            >
              <Route
                path={PageRoutes.FACTSHEET_HOME}
                element={<Navigate to={`../${PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET}`} />}
              />
              <Route path={':simpleFundKey'} element={<PMultifundFactsheetOutlet />}>
                <Route path={''} element={<PMultifundFactsheetIndex />} />
                <Route path={PageRoutes.PUBLIC_FACTSHEET_PERFORMANCE} element={<PMultifundFactsheetPerformance />} />
              </Route>
            </Route>
            {/* Temporary solution while we work on the new OPS section */}
            <Route path={PageRoutes.OPS_NEWS_OUTLET_OLD} element={<OpsTemporaryRedirectView />}>
              <Route path={'*'} element={<OpsTemporaryRedirectView />} />
            </Route>
            <Route path={PageRoutes.OPS_NEWS_OUTLET}>
              <Route path={''} element={<OpsTemporaryRedirectView />} />
              <Route path={':factsheetId'} element={requireAuth(requireStaffRole(<PPortfolioManagementOutlet />))}>
                <Route path={PageRoutes.OPS_NEWS_EVENTS} element={<PPorfolioManagementEvents />} />
                <Route path={PageRoutes.OPS_NEWS_MISC} element={<PPorfolioManagementMisc />} />
                <Route path={PageRoutes.OPS_NEWS_PERFORMANCE} element={<PPortfolioManagementPerformance />} />
                <Route path={PageRoutes.OPS_NEWS_HEDGE} element={<PPorfolioManagementHedge />} />
                <Route path={PageRoutes.OPS_NEWS_ORDERS} element={<PTradeOrdersIndex />} />
              </Route>
            </Route>
            <Route
              path={PageRoutes.PUBLISHING_OUTLET}
              element={requireAuth(
                requireStaffRole(
                  <XDOutlet
                    slug={PageRoutes.PUBLISHING_OUTLET}
                    title={localeDict?.globalSidebarPublishing ?? 'Publications'}
                  />
                )
              )}
            >
              <Route path={PageRoutes.PUBLISHING_HOME} element={<PublishingIndex />} />
              <Route
                path={PageRoutes.PUBLISHING_FS_OUTLET}
                element={<XDOutlet slug={PageRoutes.PUBLISHING_FS_OUTLET} title={'Factsheet Links'} />}
              >
                <Route path={PageRoutes.PUBLISHING_FS_OVERVIEW_INDEX} element={<FactsheetManagementIndex />} />
                <Route path={PageRoutes.PUBLISHING_FS_NEW_LINK} element={<CreateNewFactsheetLink />} />
                <Route
                  path={PageRoutes.PUBLISHING_FS_INQUIRIES_OUTLET}
                  element={
                    <XDOutlet
                      slug={PageRoutes.PUBLISHING_FS_INQUIRIES_OUTLET}
                      title={localeDict?.['fs.inquiry.links']}
                    />
                  }
                >
                  <Route path={':inquiryId'} element={<SingleAccessLinkInquiryView />} />
                </Route>
                <Route
                  path={PageRoutes.PUBLISHING_FS_OVERVIEW_OUTLET}
                  element={<XDOutlet slug={PageRoutes.PUBLISHING_FS_OVERVIEW_OUTLET} title={'Links'} />}
                >
                  <Route path={PageRoutes.PUBLISHING_FS_OVERVIEW_INDEX} element={<PFactsheetLinkIndex />} />
                  <Route path={':factsheetId'} element={<FactsheetManagementEntry />} />
                </Route>
                <Route
                  path={PageRoutes.PUBLISHING_FS_INQUIRIES_OUTLET}
                  element={
                    <XDOutlet
                      slug={PageRoutes.PUBLISHING_FS_INQUIRIES_OUTLET}
                      title={localeDict?.pubFsInquirylinkAllH1}
                    />
                  }
                >
                  <Route path={PageRoutes.PUBLISHING_FS_INQUIRIES_INDEX} element={<PFactsheetInquiryLinkIndex />} />
                </Route>
              </Route>
            </Route>
            <Route path={PageRoutes.SELF_MANAGE_ACCOUNT} element={requireAuth(<AccountSelfManagement />)}></Route>
            <Route path={PageRoutes.USERS_OUTLET} element={requireAuth(<UserManagementOutlet />, [[EUserRoles.STAFF]])}>
              <Route path={PageRoutes.USERS_HOME} element={<UserManagementIndex />} />
              <Route path={PageRoutes.USERS_OVERVIEW_OUTLET} element={<OverviewOutlet />}>
                <Route path={PageRoutes.USERS_OVERVIEW_INDEX} element={<UsersOverview />} />
                <Route path={PageRoutes.USERS_USER_HUB_OUTLET} element={<UserHubOutlet />}>
                  <Route path={PageRoutes.USERS_USER_HUB_INDEX} element={<SingleUserIndex />} />
                  <Route
                    path={PageRoutes.USERS_USER_HUB_MANAGE_ACCOUNT}
                    element={requireAuth(<UserSecurityAndPrivacy />, [[EUserRoles.ADMIN]])}
                  />
                  <Route path={PageRoutes.USERS_BACKTEST} element={<BacktestManagement />} />
                </Route>
              </Route>
              <Route path={PageRoutes.USERS_NEW} element={requireAuth(<CreateUser />, [[EUserRoles.ADMIN]])} />
              <Route
                path={PageRoutes.SIGN_UP_REQUESTS_OUTLET}
                element={requireAuth(<PSignUpRequestsOutlet />, [[EUserRoles.STAFF]])}
              >
                <Route path={PageRoutes.SIGN_UP_REQUESTS_INDEX} element={<PSignUpRequestsIndex />} />
                <Route path={':id'} element={<PSingleSignUpRequest />} />
              </Route>
            </Route>
            <Route path={PageRoutes.NOTIFICATIONS_OUTLET} element={requireAuth(<PNotificationsOutlet />)}>
              <Route path={PageRoutes.NOTIFICATIONS_INDEX} element={<PNotificationsIndex />} />
            </Route>
            <Route
              path={PageRoutes.TRADE_ORDERS_OUTLET}
              element={<Navigate to={`/${PageRoutes.OPS_NEWS_OUTLET}/uslcbt/${PageRoutes.OPS_NEWS_ORDERS}`} replace />}
            />
            <Route path={PageRoutes.PAGE_NOT_FOUND} element={<PageNotFound />} />

            {/* Playground Routes */}
            <Route path={'playground'} element={requireAuth(requireAdminRole(<PPlaygroundIndex />))} />
            <Route path={'playground-2'} element={requireAuth(requireAdminRole(<PPlaygroundIndex2 />))} />
            <Route path={'playground-3'} element={requireAuth(requireAdminRole(<PPlaygroundIndex3 />))} />
          </Route>
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default App;
